import { ChangeEvent, FC, useState } from "react";
import classNames from "classnames";
import styles from "./index.module.scss";
import Input from "@/components/input";
import { Button } from "@/components/button";
import { useTranslation } from "react-i18next";
import { isEmailAddressValid } from "@/helpers/strings";
import api from "@/services/api";
import { useMutation } from "@tanstack/react-query";
import { logFirebaseEvent } from "@/services/firebase";
import { useAppSelector } from "@/hooks/useAppSelector";
import { selectReceipt } from "@/store/receipt/selectors";

interface DigitalReceiptProps {
  className: string;
}

const DigitalReceipt: FC<DigitalReceiptProps> = ({ className }) => {
  const { t } = useTranslation();
  const receipt = useAppSelector(selectReceipt);
  const [email, setEmail] = useState("");
  const isEmailValid = isEmailAddressValid(email);

  const onSendEmail = async () => {
    if (!receipt) return;
    return await api.email.deliver(receipt.id, email);
  };

  const { mutate: sendEmail, error, data, reset } = useMutation(onSendEmail);

  const allReadySent = data !== undefined;

  const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    reset();
    setEmail(e.target.value);
  };

  const onClick = () => {
    logFirebaseEvent("receipt_emailed");
    sendEmail();
  };

  return (
    <div className={classNames(className, styles.container)}>
      <span className={classNames("h4", styles.title)}>{t("successPage.digitalReceipt")}</span>
      <div className={styles.divider} />
      <p>{t("successPage.fillInEmailForReceipt")}</p>
      <Input
        placeholder={t("ui.yourEmail")}
        containerClassName={styles.inputContainer}
        hideRemoveButton={true}
        value={email}
        onChange={onEmailChange}
        data-cy="email-input"
      />
      {allReadySent && (
        <p>
          <b>{t("successPage.hasSentReceipt")}</b>
        </p>
      )}
      <Button
        text={allReadySent ? t("successPage.emailSent") : t("successPage.sendReceipt")}
        size="x-small"
        cta={false}
        disabled={!isEmailValid || allReadySent}
        onClick={onClick}
        data-cy="send-email-button"
      />
      {!!error && <p data-cy="send-email-error">{t("successPage.emailError")}</p>}
    </div>
  );
};

export default DigitalReceipt;
